import React, { useState, useEffect } from 'react';
import { Button, Form, Modal, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import i18n from '../configs/i18next';
import informationService from '../services/rest/information';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { directionChange } from '../redux/slices/theme';
import { setLangugages } from '../redux/slices/formLang';
import languagesService from '../services/languages';

export default function LangModal({ visible, handleCancel }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { languages } = useSelector((state) => state.formLang, shallowEqual);
  const dispatch = useDispatch();

  const onFinish = async (values) => {
    const { lang } = values;
    const locale = languages.find((item) => item.locale === lang);
    const direction = lang === 'ar' || locale?.backward ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('lang', lang);
    document.documentElement.setAttribute('dir', direction);
    document.body.setAttribute('dir', direction);
    document.body.classList.remove('dir-ltr', 'dir-rtl');
    document.body.classList.add('dir-' + direction);
    setLoading(true);
    try {
      // Local translation files are the source of truth. Load them first so
      // changing language never leaves new Admin pages in English.
      try {
        const localResponse = await fetch(`/locales/${lang}/translation.json`);
        if (localResponse.ok) {
          const localTranslations = await localResponse.json();
          i18n.addResourceBundle(lang, 'translation', localTranslations, true, true);
        }
      } catch (error) {
        // The API fallback below keeps the Admin usable when a local file is unavailable.
      }

      const { data: apiTranslations } = await informationService.translations(values);
      if (apiTranslations && typeof apiTranslations === 'object') {
        const existing = i18n.getResourceBundle(lang, 'translation') || {};
        const apiOnly = Object.fromEntries(
          Object.entries(apiTranslations).filter(([key, value]) => !Object.prototype.hasOwnProperty.call(existing, key) && value),
        );
        if (Object.keys(apiOnly).length) {
          i18n.addResourceBundle(lang, 'translation', apiOnly, true, false);
        }
      }
      localStorage.setItem('i18nextLng', lang);
      await i18n.changeLanguage(lang);
      dispatch(directionChange(direction));
      handleCancel();
    } finally {
      setLoading(false);
    }
  };

  const fetchLanguages = () => {
    languagesService.getAllActive().then(({ data }) => {
      dispatch(setLangugages(data));
    });
  };

  useEffect(() => {
    fetchLanguages();
  }, []);

  return (
    <Modal
      title={t('change.language')}
      visible={visible}
      onCancel={handleCancel}
      footer={[
        <Button
          key='ok-button'
          type='primary'
          onClick={() => form.submit()}
          loading={loading}
        >
          {t('save')}
        </Button>,
        <Button key='cancel-button' onClick={handleCancel}>
          {t('cancel')}
        </Button>,
      ]}
    >
      <Form
        layout='vertical'
        name='lang-form'
        form={form}
        onFinish={onFinish}
        initialValues={{ lang: i18n.language }}
      >
        <Form.Item label={t('language')} name='lang'>
          <Select>
            {languages.map((item, idx) => (
              <Select.Option key={item.locale + idx} value={item.locale}>
                {item.title}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
}
